import React, { Component } from 'react';
import cx from 'classnames';
import { navigate } from 'gatsby';
import _get from 'lodash/get';
import _isArray from 'lodash/isArray';
import _isEqual from 'lodash/isEqual';
import { inject, observer } from 'mobx-react';
import { Container } from '../../components/Container';
import { DataTable } from '../../components/DataTable';
import { IColumnProps } from '../../components/DataTable/components/shared';
import { Grid } from '../../components/Grid';
import { GridCell } from '../../components/GridCell';
import { Icon } from '../../components/Icons';
import { LoaderFullPage } from '../../components/Loaders';
import { MediaMobile, MediaTablet, MediaDesktop } from '../../components/MediaWrapper';
import { Pagination } from '../../components/Pagination';
import { getParameterByName } from '../../utils/functions';
import { AsideMyAds } from '../AdList/components';
import { PritableContent } from './components/PrintableContent';
import { TransactionApi } from './services';
import style from './Transactions.module.css';
import { ORDERING_OPTIONS, generateTableColumns, getFilterStatusOptions } from './utils';
import {
  IOption,
  IPaginationParam,
  IPushState,
  TransactionsProps as Props,
  TransactionsState as State,
  TransactionsType,
  FilterParam,
} from './utils/types';

const TRANSACTION = {
  [TransactionsType.ORDERS]: {
    title: 'Meus pedidos',
    url: '/meus-pedidos',
    icon: 'SvgIconOrders',
    emptyState: {
      title: 'Sem Pedidos',
      description: 'Nenhum pedido foi encontrado para que seja exibido.',
    },
    activeIndex: 2,
  },
  [TransactionsType.SALES]: {
    title: 'Minhas vendas',
    url: '/minhas-vendas',
    icon: 'SvgIconMySales',
    emptyState: {
      title: 'Sem Vendas',
      description: 'Nenhuma venda foi encontrada para que seja exibida.',
    },
    activeIndex: 3,
  },
};

const TableWrapper = (props: any) => (
  <>
    <MediaMobile>
      <DataTable {...props} headless={false} />
    </MediaMobile>
    <MediaTablet>
      <DataTable {...props} headless={false} />
    </MediaTablet>
    <MediaDesktop>
      <DataTable {...props} headless={false} />
    </MediaDesktop>
  </>
);

@inject('user')
@inject('selectedStore')
@observer
export class Transactions extends Component<Props, State> {
  state = {
    isLoading: true,
    source: [],
    totalCounter: 0,
    printableData: [],
    filterStatusOptions: [],
    filterCityOptions: [],
    showNoOptions: false,
    selectedOrdering: null,
    activeFilters: [],
    orderingOptions: ORDERING_OPTIONS,
    params: {
      page: 0,
      take: 10,
      totalPages: 0,
      searchBoxValue: '',
      selectedStatusType: { label: '', value: null },
      selectedOrdering: {
        id: null,
        label: null,
        type: null,
        value: null,
        isChecked: null,
      },
    },
  };

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (prevProps.locale !== this.props.locale) {
      this.updatePage();
    }
  }

  componentDidMount() {
    this.updatePage();
  }

  getPageOnQueryString = () => {
    const queryString = (param: any) => getParameterByName(param);
    const selectedPage = queryString('page') ? parseInt(queryString('page'), 10) : 0;
    const { params } = this.state;
    return {
      ...params,
      page: selectedPage > 0 ? selectedPage - 1 : 0,
    };
  };

  updatePage() {
    const params = this.getPageOnQueryString();
    this.setState(
      {
        filterStatusOptions: getFilterStatusOptions(),
        params,
      },
      () => {
        this.getSource();
      },
    );
  }

  getSortingParams = () => {
    const {
      params: { selectedOrdering = { type: '', value: null } },
    } = this.state;

    const { type, value } = selectedOrdering;
    if (type && value) {
      return {
        [type]: value,
      };
    }
  };

  getSource = () => {
    const {
      params: { page, take, searchBoxValue, selectedStatusType },
    } = this.state;

    const normalizedParams = {
      page,
      take,
      search: searchBoxValue,
      status: null,
      order: this.getSortingParams(),
    };

    const filters = [];

    if (selectedStatusType && selectedStatusType.value) {
      normalizedParams.status = selectedStatusType.value;
      filters.push({
        type: 'status',
        active: selectedStatusType,
      });
    }

    filters.push({
      type: 'text',
      active: { label: searchBoxValue, value: searchBoxValue },
    });

    const calcTotalPages = (totalResults: number) => {
      const calc = Math.ceil(totalResults / take);
      if (isNaN(calc)) {
        return 0;
      }
      return calc;
    };

    this.setState(
      {
        isLoading: true,
        activeFilters: filters,
      },
      () => {
        const { user, type: transactionType } = this.props;
        TransactionApi.get({
          ...normalizedParams,
          userId: user.profile && user.profile.id,
          transactionType,
        }).then((response: any) => {
          const { data, headers } = response;
          this.setState(({ params }) => ({
            source: data,
            totalCounter: headers['content-range'],
            params: {
              ...params,
              totalPages: calcTotalPages(headers['content-range']),
            },
            isLoading: false,
          }));
        });
      },
    );
  };

  pushStateQueryString = (pushState: IPushState) => {
    let { selectedPage } = pushState;
    const { type } = this.props;
    const {
      params: { page },
    } = this.state;
    selectedPage = selectedPage || page;
    const queryParam = `page=${selectedPage + 1}`;

    navigate(`/${TRANSACTION[type].url}?${queryParam}`, { state: { lastUrl: queryParam } });
  };

  handlePaginationClick = (arSelected: IPaginationParam[]) => {
    const { params } = this.state;
    const selectedPage = _get(arSelected, 'selected', 0);

    this.setState(
      {
        params: {
          ...params,
          page: selectedPage,
        },
      },
      () => {
        this.pushStateQueryString({ selectedPage });
        this.getSource();
      },
    );
  };

  handlePrintAction = (values: any | any[]) => {
    const printableData = _isArray(values) ? values : [values];
    this.setState(
      {
        printableData,
      },
      () => {
        if (typeof window !== 'undefined') {
          window.print();
        }
      },
    );
  };

  handleAllPrintAction = () => {
    this.setState(
      {
        isLoading: true,
      },
      () => {
        const { user, type: transactionType } = this.props;
        TransactionApi.get({
          take: 999999,
          page: 0,
          userId: user.profile && user.profile.id,
          transactionType,
        })
          .then((response: any) => {
            const { data } = response;
            this.setState(
              {
                printableData: data,
                isLoading: false,
              },
              () => {
                if (typeof window !== 'undefined') {
                  window.print();
                }
              },
            );
          })
          .catch(err => {
            throw new Error(err);
          });
      },
    );
  };

  handleOrdering = (option: IOption) => {
    this.setState(
      ({ params, orderingOptions }) => {
        const { page } = params;
        const activeOrdering = orderingOptions.map((item: IOption) => {
          item.isChecked = false;
          if (_isEqual(item, option)) {
            item.isChecked = true;
          }
          return item;
        });
        return {
          orderingOptions: activeOrdering,
          params: {
            ...params,
            page,
            selectedOrdering: option,
          },
        };
      },
      () => {
        this.getSource();
      },
    );
  };

  handleOnMobileFilter = (isReset?: boolean) => {
    const { params } = this.state;
    const cloneParams = params;
    if (isReset) {
      cloneParams.page = 0;
      cloneParams.searchBoxValue = '';
      cloneParams.selectedStatusType = { label: '', value: null };
      this.setState({
        params: cloneParams,
      });
    }
    this.getSource();
  };

  handleStatusFilter = (option: FilterParam, hasOutsideControl?: boolean) => {
    const { params } = this.state;
    const normalizeOption = option;
    const { page } = params;
    if (hasOutsideControl) {
      return this.setState(
        {
          params: {
            ...params,
            page,
            selectedStatusType: normalizeOption,
          },
        },
        () => {
          this.pushStateQueryString({ type: normalizeOption.label, selectedPage: page });
        },
      );
    }

    return this.setState(
      {
        params: {
          ...params,
          page,
          selectedStatusType: normalizeOption,
        },
      },
      () => {
        this.getSource();
        this.pushStateQueryString({ type: normalizeOption.label, selectedPage: page });
      },
    );
  };

  handleTableSearch = (value: string) => {
    const { params } = this.state;
    const { page } = params;

    this.setState(
      {
        ...this.state,
        params: {
          ...params,
          page,
          searchBoxValue: value,
        },
      },
      () => {
        this.getSource();
      },
    );
  };

  finalizeOrder = async (id: string | number) => {
    await TransactionApi.complete({ orderId: id });
    this.getSource();
  };

  render() {
    const {
      isLoading,
      source,
      totalCounter,
      printableData,
      orderingOptions,
      params: { page, totalPages, searchBoxValue, selectedStatusType },
      filterStatusOptions,
    } = this.state;

    if (isLoading) {
      return <LoaderFullPage />;
    }

    const { type, user, selectedStore } = this.props;

    const sortingDetails = {
      label: 'Ordenar',
      icon: 'SvgIconList',
      options: orderingOptions,
      onRequestChange: this.handleOrdering,
    };

    const tableColumns = generateTableColumns({
      numberOfRows: totalCounter,
      sortingDetails,
      onPrintAllRows: this.handleAllPrintAction,
      onPrintSelectedRows: this.handlePrintAction,
      onFinalizeOrder: this.finalizeOrder,
    });

    const dataTableEmptyState = {
      image: `${process.env.FTP_IMAGENS}images/search/empty-state.png`,
      ...TRANSACTION[type].emptyState,
    };

    const handleRemoveFilter = (val: any) => {
      const { type: typeRemovedFilter } = val;
      const { params } = this.state;
      const { page: pageRemovedFilter } = params;
      if (typeRemovedFilter === 'text') {
        this.setState(
          {
            ...this.state,
            params: {
              ...params,
              searchBoxValue: '',
            },
          },
          () => {
            this.pushStateQueryString({ user: '', selectedPage: pageRemovedFilter });
            this.getSource();
          },
        );
      }

      if (typeRemovedFilter === 'status') {
        this.setState(
          {
            ...this.state,
            params: {
              ...params,
              selectedStatusType: { label: '', value: null },
            },
          },
          () => {
            this.pushStateQueryString({ type: '', selectedPage: pageRemovedFilter });
            this.getSource();
          },
        );
      }
    };

    const handleRemoveAllFilters = () => {
      const { params } = this.state;
      const { page: pageRemoveAllFilters } = params;
      this.setState(
        {
          ...this.state,
          params: {
            ...params,
            page: 0,
            searchBoxValue: '',
            selectedStatusType: { label: '', value: null },
          },
        },
        () => {
          this.pushStateQueryString({
            user: '',
            type: null,
            location: '',
            selectedPage: pageRemoveAllFilters,
          });
          this.getSource();
        },
      );
    };

    const dataTableResumeProps = {
      info: {
        label:
          totalCounter > 1 ? 'resultados' : totalCounter === 0 ? 'nenhum resultado' : 'resultado',
        counter: totalCounter,
      },
      handleRemoveFilter,
      handleRemoveAllFilters,
      activeFilters: this.state.activeFilters,
    };

    const pagination = totalPages !== 0 && (
      <section className="pt40 pb40 bg-wild-sand">
        <Pagination
          marginClass="ma0"
          forcePage={page}
          onClick={this.handlePaginationClick}
          totalPages={totalPages}
        />
      </section>
    );

    const userToEdit =
      user.profile.admin && selectedStore ? selectedStore.clientStore : user.profile;

    const fantasyName = userToEdit ? userToEdit.fantasyName : '';
    const name = userToEdit ? userToEdit.name : '';
    const frontagePhoto = userToEdit ? userToEdit.urlFrontagePhoto : '';
    const logoPhoto = userToEdit ? userToEdit.urlCompanyLogo : '';

    const cxGrid = cx(
      style.customPaddingTop,
      'flex justify-center justify-start-m justify-start-l mb24 pt32-m',
    );

    const dataTableSearchProps = {
      placeholder: 'Pesquise por produto',
      handleChange: this.handleTableSearch,
      initialValue: searchBoxValue,
    };

    const dataTableFiltersProps = {
      mobileLabel: 'Filtrar',
      mobileIcon: 'SvgIconFilter',
      onMobileFilter: this.handleOnMobileFilter,
      options: [
        {
          title: 'Status',
          placeholder: 'Selecione um status',
          selected: selectedStatusType,
          isSearchable: false,
          options: filterStatusOptions,
          onRequestChange: this.handleStatusFilter,
        },
      ],
    };

    return (
      <>
        <section className="noPrint">
          <Container>
            <Grid className={cxGrid}>
              <GridCell
                width={[12 / 12, 10 / 12, 3 / 12, 3 / 12]}
                className="noPrint pb32-ns pb0-m pr24-m pr24-l flex"
              >
                <div className="flex flex-auto mb32-m">
                  <AsideMyAds
                    name={fantasyName || name}
                    urlFrontagePhoto={frontagePhoto}
                    urlCompanyLogo={logoPhoto}
                    hideMenuOnTablet
                    activeIndex={TRANSACTION[type].activeIndex}
                    isAdmin={user.profile.admin}
                  />
                </div>
              </GridCell>
              <GridCell width={[12 / 12, 10 / 12, 9 / 12, 9 / 12]}>
                <h1 className="trout f20 f30-m lh-copy lh-paragraph-m line-height fw7 ttu flex items-center mb24">
                  <Icon
                    className={style.customIcon}
                    customColor="#eb0000"
                    name={TRANSACTION[type].icon}
                  />
                  <span className="ml8">{TRANSACTION[type].title}</span>
                </h1>
                <TableWrapper
                  specialMobileField="status"
                  source={source}
                  columns={tableColumns as IColumnProps[]}
                  emptyState={dataTableEmptyState}
                  searchContainer={dataTableSearchProps}
                  filtersContainer={dataTableFiltersProps}
                  orderingContainer={sortingDetails}
                  resumeContainer={dataTableResumeProps}
                  hasInnerContainer
                />

                {pagination}
              </GridCell>
            </Grid>
          </Container>
        </section>
        <PritableContent data={printableData} />
      </>
    );
  }
}
